import React, { useState, useEffect } from 'react';
import { useQuery, useLazyQuery } from '@/apollo';
import { gql } from '@/apollo';
import { MapPin, Shield, Stethoscope, Phone, Mail, ExternalLink, Users, Clock, AlertCircle, FileText, Building, Search, Languages, Award, UserCheck, ChevronDown } from 'lucide-react';
import Select from 'react-select';
import { AgeInput } from '../moderation/shared/AgeInput';
import { RichTextDisplay } from '../ui/RichTextDisplay';
import { CollapsibleRichText } from '../ui/CollapsibleRichText';

// GraphQL Queries - Using the same data source as Moderator Dashboard
const GET_INSURANCES = gql`
  query GetInsurances($customerId: ID) {
    insurances(customerId: $customerId) {
      id
      name
      description
      isActive
    }
  }
`;

const GET_LOCATIONS = gql`
  query GetLocations($where: LocationWhereInput) {
    locations(where: $where) {
      id
      name
      state {
        id
        code
        name
      }
      zipCode
      physicalAddress
      isActive
    }
  }
`;

const GET_CONDITIONS = gql`
  query GetConditions {
    conditions {
      id
      name
      category
      description
      isActive
    }
  }
`;

const GET_LANGUAGES = gql`
  query GetLanguages {
    languages {
      id
      code
      name
      isActive
    }
  }
`;

const GET_CUSTOMERS = gql`
  query GetCustomers {
    customers {
      id
      name
      description
      isActive
    }
  }
`;

const FIND_PROVIDERS = gql`
  query FindProviders($insurance: ID, $location: ID, $condition: ID, $customer: ID, $age: Int, $language: ID) {
    findProviders(insurance: $insurance, location: $location, condition: $condition, customer: $customer, age: $age, language: $language) {
      provider {
        id
        firstName
        lastName
        title
        phone
        email
        website
        licenseNumber
        npiNumber
        acceptsNewPatients
        schedulingInstructions
        requiresReferral
        insuranceNotes
        isActive
        minimumAge
        maximumAge
        ageRestrictionNotes
        officeSuite
        officePhone
        clinicalPhone
        frontDeskPhone
        maLvnName
        maLvnPhone
        triageNurseName
        triageNursePhone
        surgerySchedulerName
        surgerySchedulerPhone
        practiceAdminName
        practiceAdminPhone
        phoneNumbers {
          id
          name
          phone
          location {
            id
            name
            city
            state {
              id
              code
              name
            }
            zipCode
            physicalAddress
          }
        }
        insurances {
          id
          insurance {
            id
            name
            description
          }
          notes
        }
        conditions {
          id
          condition {
            id
            name
            category
            description
          }
          notes
        }
        locations {
          id
          location {
            id
            name
            city
            state {
              id
              code
              name
            }
            physicalAddress
            zipCode
          }
          address
          suite
          phone
        }
        languages {
          id
          language {
            id
            name
            code
          }
        }
        notes {
          id
          content
          category
          order
          createdAt
        }
        apps {
          id
          firstName
          lastName
          title
        }
        supervisingProvider {
          id
          firstName
          lastName
          title
        }
        supervisingProviders {
          id
          firstName
          lastName
          title
        }
      }
      relevantLocation {
        id
        name
        city
        state {
          id
          code
          name
        }
        zipCode
        physicalAddress
      }
      displayNotes
      priority
    }
  }
`;



interface ProviderSelectionProps {}

interface SelectOption {
  value: string;
  label: string;
  data?: any;
}

interface ProviderResult {
  provider: {
    id: string;
    firstName: string;
    lastName: string;
    title?: string;
    phone?: string;
    email?: string;
    website?: string;
    licenseNumber?: string;
    npiNumber?: string;
    acceptsNewPatients: boolean;
    schedulingInstructions?: string;
    requiresReferral: boolean;
    insuranceNotes?: string;
    minimumAge?: number;
    maximumAge?: number;
    ageRestrictionNotes?: string;
    officeSuite?: string;
    officePhone?: string;
    clinicalPhone?: string;
    frontDeskPhone?: string;
    maLvnName?: string;
    maLvnPhone?: string;
    triageNurseName?: string;
    triageNursePhone?: string;
    surgerySchedulerName?: string;
    surgerySchedulerPhone?: string;
    practiceAdminName?: string;
    practiceAdminPhone?: string;
    phoneNumbers?: {
      id: string;
      name: string;
      phone: string;
      location?: {
        id: string;
        name: string;
        city?: string;
        physicalAddress?: string;
        zipCode?: string;
        state?: {
          id: string;
          code: string;
          name: string;
        };
      };
    }[];
    insurances?: {
      id: string;
      insurance: {
        id: string;
        name: string;
        description?: string;
      };
      notes?: string;
    }[];
    conditions?: {
      id: string;
      condition: {
        id: string;
        name: string;
        category?: string;
        description?: string;
      };
      notes?: string;
    }[];
    locations?: {
      id: string;
      location: {
        id: string;
        name: string;
        city?: string;
        state?: {
          id: string;
          code: string;
          name: string;
        };
        physicalAddress?: string;
        zipCode?: string;
      };
      address?: string;
      suite?: string;
      phone?: string;
    }[];
    languages?: {
      id: string;
      language: {
        id: string;
        name: string;
        code: string;
      };
    }[];
    notes?: {
      id: string;
      content: string;
      category?: string;
      order: number;
      createdAt: string;
    }[];
    apps?: {
      id: string;
      firstName: string;
      lastName: string;
      title?: string;
    }[];
    supervisingProvider?: {
      id: string;
      firstName: string;
      lastName: string;
      title?: string;
    };
  };
  relevantLocation?: {
    id: string;
    name: string;
    city?: string;
    state?: {
      id: string;
      code: string;
      name: string;
    };
    zipCode?: string;
    physicalAddress?: string;
  };
  displayNotes?: string;
  priority: number;
}

const formatPhone = (phone?: string) => {
  if (!phone) return null;
  
  // Remove all non-numeric characters
  const cleaned = phone.replace(/\D/g, '');
  
  // Format as (XXX) XXX-XXXX
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  
  return phone;
};

export const ProviderSelection: React.FC<ProviderSelectionProps> = () => {
  const [selectedCustomer, setSelectedCustomer] = useState<SelectOption | null>(null);
  const [selectedInsurance, setSelectedInsurance] = useState<SelectOption | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<SelectOption | null>(null);
  const [selectedCondition, setSelectedCondition] = useState<SelectOption | null>(null);
  const [selectedLanguage, setSelectedLanguage] = useState<SelectOption | null>(null);
  const [age, setAge] = useState<number | null>(null);
  const [collapsedProviders, setCollapsedProviders] = useState<Set<string>>(new Set());
  const [selectedCategory, setSelectedCategory] = useState<string>('');

  // Data queries - Using the same data source as Moderator Dashboard
  const { data: customersData, loading: customersLoading } = useQuery(GET_CUSTOMERS, { fetchPolicy: 'network-only' });
  const { data: insurancesData, loading: insurancesLoading } = useQuery(GET_INSURANCES, {
    fetchPolicy: 'network-only',
    variables: { customerId: selectedCustomer?.value ?? null },
    skip: !selectedCustomer?.value
  });
  const { data: locationsData, loading: locationsLoading } = useQuery(GET_LOCATIONS, {
    fetchPolicy: 'network-only',
    variables: { 
      where: selectedCustomer?.value ? { customerId: selectedCustomer.value } : {} 
    },
    skip: false // Always fetch, but filter by customer if selected
  });
  const { data: conditionsData, loading: conditionsLoading } = useQuery(GET_CONDITIONS, { fetchPolicy: 'network-only' });
  const { data: languagesData, loading: languagesLoading } = useQuery(GET_LANGUAGES, { fetchPolicy: 'network-only' });
  
  // Query providers for selected customer to get their conditions
  const [getCustomerProviders, { data: customerProvidersData }] = useLazyQuery(FIND_PROVIDERS, {
    fetchPolicy: 'network-only'
  });

  // Fetch providers for customer when customer is selected
  React.useEffect(() => {
    if (selectedCustomer?.value) {
      getCustomerProviders({
        variables: {
          customer: selectedCustomer.value
        }
      });
    }
  }, [selectedCustomer, getCustomerProviders]);

  // Provider search
  const [findProviders, { data: providersData, loading: providersLoading, error: providersError }] = useLazyQuery(FIND_PROVIDERS, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true
  });

  // Transform data for react-select - Only show active items
  const customerOptions: SelectOption[] = customersData?.customers
    ?.filter((customer: any) => customer.isActive)
    ?.map((customer: any) => ({
      value: customer.id,
      label: customer.name,
      data: customer
    })) || [];

  const insuranceOptions: SelectOption[] = insurancesData?.insurances
    ?.filter((insurance: any) => insurance.isActive)
    ?.map((insurance: any) => ({
      value: insurance.id,
      label: insurance.name,
      data: insurance
    })) || [];

  const locationOptions: SelectOption[] = locationsData?.locations
    ?.filter((location: any) => location.isActive)
    ?.map((location: any) => ({
      value: location.id,
      label: `${location.name}${location.state ? `, ${location.state.code}` : ''}`,
      data: location
    })) || [];

  // Clear location selection when customer changes
  React.useEffect(() => {
    if (selectedCustomer && selectedLocation) {
      // Check if selected location belongs to the selected customer
      const locationBelongsToCustomer = locationOptions.some(
        (option) => option.value === selectedLocation.value
      );
      if (!locationBelongsToCustomer) {
        setSelectedLocation(null);
      }
    } else if (!selectedCustomer) {
      // Clear location if no customer is selected
      setSelectedLocation(null);
    }
  }, [selectedCustomer, locationOptions, selectedLocation]);

  // Clear insurance selection when customer changes or selected insurance is not in filtered list
  React.useEffect(() => {
    if (!selectedCustomer) {
      setSelectedInsurance(null);
      return;
    }
    if (selectedInsurance && insuranceOptions.length > 0) {
      const insuranceInList = insuranceOptions.some((opt) => opt.value === selectedInsurance.value);
      if (!insuranceInList) {
        setSelectedInsurance(null);
      }
    }
  }, [selectedCustomer, insuranceOptions, selectedInsurance]);

  // Extract unique condition IDs from providers for selected customer
  const customerConditionIds = React.useMemo(() => {
    if (!selectedCustomer?.value || !customerProvidersData?.findProviders) {
      return new Set<string>();
    }
    const conditionIds = new Set<string>();
    customerProvidersData.findProviders.forEach((result: any) => {
      result.provider.conditions?.forEach((providerCondition: any) => {
        if (providerCondition.condition?.id) {
          conditionIds.add(providerCondition.condition.id);
        }
      });
    });
    return conditionIds;
  }, [selectedCustomer, customerProvidersData]);

  // Extract unique categories from conditions (filtered by customer if customer is selected)
  const categoryOptions = React.useMemo(() => {
    const categories = new Set<string>();
    let conditionsToCheck = conditionsData?.conditions?.filter((condition: any) => condition.isActive) || [];
    
    // If customer is selected, only check conditions used by that customer's providers
    if (selectedCustomer?.value && customerConditionIds.size > 0) {
      conditionsToCheck = conditionsToCheck.filter((condition: any) => 
        customerConditionIds.has(condition.id)
      );
    }
    
    conditionsToCheck
      ?.filter((condition: any) => condition.category)
      ?.forEach((condition: any) => {
        categories.add(condition.category);
      });
    return Array.from(categories).sort();
  }, [conditionsData, selectedCustomer, customerConditionIds]);

  // Filter conditions based on category and customer
  const filteredConditionOptions: SelectOption[] = React.useMemo(() => {
    let filtered = conditionsData?.conditions
      ?.filter((condition: any) => condition.isActive) || [];

    // Filter by customer: only show conditions used by providers for selected customer
    if (selectedCustomer?.value && customerConditionIds.size > 0) {
      filtered = filtered.filter((condition: any) => 
        customerConditionIds.has(condition.id)
      );
    }

    // Filter by category
    if (selectedCategory) {
      filtered = filtered.filter((condition: any) => condition.category === selectedCategory);
    }

    return filtered.map((condition: any) => ({
      value: condition.id,
      label: condition.name,
      data: condition
    }));
  }, [conditionsData, selectedCategory, selectedCustomer, customerConditionIds]);

  const conditionOptions: SelectOption[] = filteredConditionOptions;

  const languageOptions: SelectOption[] = languagesData?.languages
    ?.filter((language: any) => language.isActive)
    ?.map((language: any) => ({
      value: language.id,
      label: language.name,
      data: language
    })) || [];

  // Clear selected condition if it's no longer in the filtered list
  useEffect(() => {
    if (selectedCondition) {
      const isStillAvailable = conditionOptions.some(
        (option) => option.value === selectedCondition.value
      );
      if (!isStillAvailable) {
        setSelectedCondition(null);
      }
    }
  }, [conditionOptions, selectedCondition]);

  // Handle manual search button click - customer is required, other fields are optional
  const handleSearch = () => {
    if (!selectedCustomer) {
      return;
    }
    
    const variables: any = {
      customer: selectedCustomer.value
    };
    
    // Only include other variables if they are provided
    if (selectedInsurance) {
      variables.insurance = selectedInsurance.value;
    }
    if (selectedLocation) {
      variables.location = selectedLocation.value;
    }
    if (selectedCondition) {
      variables.condition = selectedCondition.value;
    }
    if (age !== null && age !== undefined) {
      variables.age = age;
    }
    if (selectedLanguage) {
      variables.language = selectedLanguage.value;
    }
    
    findProviders({ variables });
  };

  // Check if search button should be enabled (customer is required)
  const canSearch = selectedCustomer !== null;

  // Get providers data and filter out inactive providers
  const filteredProviders = (providersData?.findProviders || []).filter((result: any) => result.provider.isActive);

  // Initialize all providers as expanded when they load (default to expanded)
  // Providers are expanded by default unless they're in the collapsedProviders set

  const resetFilters = () => {
    setSelectedCustomer(null);
    setSelectedInsurance(null);
    setSelectedLocation(null);
    setSelectedCondition(null);
    setSelectedLanguage(null);
    setAge(null);
    setSelectedCategory('');
  };

  // Clear condition filters when customer changes
  useEffect(() => {
    setSelectedCategory('');
    setSelectedCondition(null);
  }, [selectedCustomer]);

  const selectStyles = {
    control: (provided: any, state: any) => ({
      ...provided,
      minHeight: '44px',
      backgroundColor: state.isDisabled 
        ? 'var(--color-bg-tertiary)' 
        : 'var(--color-bg-secondary)',
      borderColor: state.isDisabled 
        ? 'var(--color-border-muted)' 
        : 'var(--color-border)',
      color: state.isDisabled 
        ? 'var(--color-text-muted)' 
        : 'var(--color-text-primary)',
      opacity: state.isDisabled ? 0.6 : 1,
      cursor: state.isDisabled ? 'not-allowed' : 'default',
      '&:hover': {
        borderColor: state.isDisabled 
          ? 'var(--color-border-muted)' 
          : 'var(--color-border-hover)'
      },
      '&:focus-within': {
        borderColor: state.isDisabled 
          ? 'var(--color-border-muted)' 
          : 'var(--color-accent)',
        boxShadow: state.isDisabled 
          ? 'none' 
          : '0 0 0 1px var(--color-accent)'
      }
    }),
    menu: (provided: any) => ({
      ...provided,
      backgroundColor: 'var(--color-bg-primary)',
      border: '1px solid var(--color-border)',
      boxShadow: 'var(--shadow-lg)'
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: state.isSelected 
        ? 'var(--color-accent)' 
        : state.isFocused 
        ? 'var(--color-bg-secondary)' 
        : 'var(--color-bg-primary)',
      color: state.isSelected ? 'white' : 'var(--color-text-primary)',
      '&:hover': {
        backgroundColor: state.isSelected ? 'var(--color-accent-hover)' : 'var(--color-bg-secondary)'
      }
    }),
    singleValue: (provided: any, state: any) => ({
      ...provided,
      color: state.isDisabled 
        ? 'var(--color-text-muted)' 
        : 'var(--color-text-primary)'
    }),
    placeholder: (provided: any, state: any) => ({
      ...provided,
      color: state.isDisabled 
        ? 'var(--color-text-muted)' 
        : 'var(--color-text-muted)'
    }),
    input: (provided: any) => ({
      ...provided,
      color: 'var(--color-text-primary)'
    }),
    indicatorSeparator: (provided: any) => ({
      ...provided,
      backgroundColor: 'var(--color-border)'
    }),
    dropdownIndicator: (provided: any, state: any) => ({
      ...provided,
      color: state.isDisabled 
        ? 'var(--color-text-muted)' 
        : 'var(--color-text-secondary)',
      opacity: state.isDisabled ? 0.5 : 1,
      '&:hover': {
        color: state.isDisabled 
          ? 'var(--color-text-muted)' 
          : 'var(--color-text-primary)'
      }
    }),
    clearIndicator: (provided: any, state: any) => ({
      ...provided,
      color: state.isDisabled 
        ? 'var(--color-text-muted)' 
        : 'var(--color-text-secondary)',
      opacity: state.isDisabled ? 0.5 : 1,
      '&:hover': {
        color: state.isDisabled 
          ? 'var(--color-text-muted)' 
          : 'var(--color-text-primary)'
      }
    })
  };

  return (
    <div className="space-y-6">
      {/* Standalone Customer Filter */}
      <div 
        className="rounded-xl shadow-sm border p-6"
        style={{
          backgroundColor: 'var(--color-bg-primary)',
          borderColor: 'var(--color-border)',
          boxShadow: 'var(--shadow-sm)'
        }}
      >
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>
              <Building className="w-4 h-4 inline mr-2" />
              Choose Customer Organization
            </label>
            <button
              onClick={resetFilters}
              disabled={!selectedCustomer}
              className="text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ color: selectedCustomer ? 'var(--color-accent)' : 'var(--color-text-muted)' }}
              onMouseEnter={(e) => {
                if (selectedCustomer) {
                  e.currentTarget.style.color = 'var(--color-accent-hover)';
                }
              }}
              onMouseLeave={(e) => {
                if (selectedCustomer) {
                  e.currentTarget.style.color = 'var(--color-accent)';
                }
              }}
            >
              Clear All Filters
            </button>
          </div>
          <Select
            value={selectedCustomer}
            onChange={setSelectedCustomer}
            options={customerOptions}
            placeholder="Select customer..."
            isLoading={customersLoading}
            styles={selectStyles}
            isClearable
            isSearchable
          />
          <p className="text-sm mt-2" style={{ color: 'var(--color-text-muted)' }}>
            Select your organization to access provider information
          </p>
        </div>
      </div>

      {/* Provider Content */}
        <div>
          {/* Provider Filter Criteria */}
          <div 
            className="rounded-xl shadow-sm border p-6 mb-6"
            style={{
              backgroundColor: 'var(--color-bg-primary)',
              borderColor: 'var(--color-border)',
              boxShadow: 'var(--shadow-sm)'
            }}
          >
            <h2 className="text-lg font-semibold mb-4" style={{ color: 'var(--color-text-primary)' }}>Provider Search Criteria</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          {/* Insurance Selection */}
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>
              <Shield className="w-4 h-4 inline mr-2" />
              Choose Insurance
            </label>
            <Select
              options={insuranceOptions}
              value={selectedInsurance}
              onChange={setSelectedInsurance}
              placeholder="Select insurance..."
              isClearable
              isSearchable
              isDisabled={!selectedCustomer}
              styles={selectStyles}
              className="text-sm"
              isLoading={insurancesLoading}
            />
            {insurancesLoading && (
              <p className="text-sm mt-2" style={{ color: 'var(--color-text-muted)' }}>Loading insurances...</p>
            )}
          </div>

          {/* Location Selection */}
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>
              <MapPin className="w-4 h-4 inline mr-2" />
              Choose Location
            </label>
            <Select
              options={locationOptions}
              value={selectedLocation}
              onChange={setSelectedLocation}
              placeholder="Select location..."
              isClearable
              isSearchable
              isDisabled={!selectedCustomer}
              styles={selectStyles}
              className="text-sm"
              isLoading={locationsLoading}
            />
            {locationsLoading && (
              <p className="text-sm mt-2" style={{ color: 'var(--color-text-muted)' }}>Loading locations...</p>
            )}
          </div>
          </div>

        {/* Condition Selection - Full Width */}
        <div className="mb-4">
            <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>
              <Stethoscope className="w-4 h-4 inline mr-2" />
              Choose Condition/Diagnosis
            </label>
          <div className="flex items-stretch gap-3">
            <div className="w-64 flex-shrink-0 relative">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                disabled={!selectedCustomer}
                className="w-full h-full pl-3 pr-10 py-2 border rounded-lg focus:ring-2 transition-colors appearance-none text-sm"
                style={{
                  backgroundColor: !selectedCustomer ? 'var(--color-bg-disabled)' : 'var(--color-bg-secondary)',
                  borderColor: !selectedCustomer ? 'var(--color-border-muted)' : 'var(--color-border)',
                  color: !selectedCustomer ? 'var(--color-text-disabled)' : 'var(--color-text-primary)',
                  cursor: !selectedCustomer ? 'not-allowed' : 'pointer',
                  minHeight: '44px',
                  '--tw-ring-color': 'var(--color-accent)',
                } as React.CSSProperties}
              >
                <option value="">All Categories</option>
                {categoryOptions.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 pointer-events-none" style={{ color: !selectedCustomer ? 'var(--color-text-disabled)' : 'var(--color-text-muted)' }} />
            </div>
            <div className="flex-1 min-w-0">
            <Select
              options={conditionOptions}
              value={selectedCondition}
              onChange={setSelectedCondition}
                placeholder="Search conditions..."
              isClearable
              isSearchable
              isDisabled={!selectedCustomer}
              styles={selectStyles}
              className="text-sm"
              isLoading={conditionsLoading}
            />
            </div>
          </div>
            {conditionsLoading && (
              <p className="text-sm mt-2" style={{ color: 'var(--color-text-muted)' }}>Loading conditions...</p>
            )}
        </div>

        {/* Age and Language Selection */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <AgeInput
            label="Patient Age"
            value={age}
            onChange={setAge}
            placeholder="e.g., 25"
            min={0}
            max={120}
            disabled={!selectedCustomer}
          />
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>
              <Languages className="w-4 h-4 inline mr-2" />
              Choose Language
            </label>
            <Select
              options={languageOptions}
              value={selectedLanguage}
              onChange={setSelectedLanguage}
              placeholder="Select language..."
              isClearable
              isSearchable
              isDisabled={!selectedCustomer}
              styles={selectStyles}
              className="text-sm"
              isLoading={languagesLoading}
            />
            {languagesLoading && (
              <p className="text-sm mt-2" style={{ color: 'var(--color-text-muted)' }}>Loading languages...</p>
            )}
          </div>
        </div>

        {/* Search Button */}
        <div className="mb-4 flex justify-center">
          <button
            onClick={handleSearch}
            disabled={!canSearch || providersLoading}
            className="w-full px-6 py-3 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              backgroundColor: canSearch && !providersLoading ? '#1e40af' : 'var(--color-bg-tertiary)',
              color: canSearch && !providersLoading ? '#ffffff' : 'var(--color-text-muted)',
              fontWeight: canSearch && !providersLoading ? '600' : '400'
            }}
            onMouseEnter={(e) => {
              if (canSearch && !providersLoading) {
                e.currentTarget.style.backgroundColor = '#1e3a8a';
              }
            }}
            onMouseLeave={(e) => {
              if (canSearch && !providersLoading) {
                e.currentTarget.style.backgroundColor = '#1e40af';
              }
            }}
          >
            <Search className="w-4 h-4" />
            <span>{providersLoading ? 'Searching...' : 'Search Providers'}</span>
          </button>
        </div>
      </div>

      {/* Results Section */}
      <div>
        <div className="py-2">
          {providersLoading && (
            <div className="text-center py-8">
              <div 
                className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto"
                style={{ borderColor: 'var(--color-accent)' }}
              ></div>
              <p className="mt-2" style={{ color: 'var(--color-text-secondary)' }}>Searching providers...</p>
            </div>
          )}

          {providersError && (
            <div className="text-center py-8">
              <AlertCircle className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--color-error)' }} />
              <p style={{ color: 'var(--color-error)' }}>Error loading providers. Please try again.</p>
            </div>
          )}

          {!providersLoading && !providersError && filteredProviders.length === 0 && selectedInsurance && selectedLocation && selectedCondition && (
            <div className="text-center py-8">
              <Users className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--color-text-muted)' }} />
              <p style={{ color: 'var(--color-text-secondary)' }}>No providers found matching your criteria.</p>
              <p className="text-sm mt-1" style={{ color: 'var(--color-text-muted)' }}>Try adjusting your filters.</p>
            </div>
          )}

          {!providersData && !providersLoading && (
            <div className="text-center py-8">
              <Search className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--color-text-muted)' }} />
              <p style={{ color: 'var(--color-text-secondary)' }}>No search results yet.</p>
              <p className="text-sm mt-1" style={{ color: 'var(--color-text-muted)' }}>Select any combination of filters and click "Search Providers" to find providers.</p>
            </div>
          )}

          {/* Provider Cards */}
          {filteredProviders.length > 0 && (
            <div className="space-y-6">
              {filteredProviders.map((result: ProviderResult) => {
                const isExpanded = !collapsedProviders.has(result.provider.id);
                const toggleExpanded = () => {
                  setCollapsedProviders(prev => {
                    const newSet = new Set(prev);
                    if (newSet.has(result.provider.id)) {
                      newSet.delete(result.provider.id);
                    } else {
                      newSet.add(result.provider.id);
                    }
                    return newSet;
                  });
                };

                return (
                <div 
                  key={result.provider.id} 
                  className="border rounded-2xl p-8 hover:shadow-lg transition-all duration-200"
                  style={{
                    borderColor: 'var(--color-border)',
                    backgroundColor: 'var(--color-bg-primary)',
                    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                  }}
                >
                  {/* Header Section - Clickable */}
                  <div 
                    className="flex justify-between items-start mb-6 pb-6 border-b cursor-pointer"
                    style={{ borderColor: 'var(--color-border)' }}
                    onClick={toggleExpanded}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <ChevronDown 
                          className={`w-5 h-5 transition-transform duration-200 flex-shrink-0`}
                          style={{ 
                            color: 'var(--color-text-secondary)',
                            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
                          }}
                        />
                        <h3 className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
                          {result.provider.firstName} {result.provider.lastName}
                          {result.provider.title ? `, ${result.provider.title}` : ''}
                        </h3>
                      </div>
                      <div className="flex-1">
                      <div className="space-y-1">
                        {result.provider.conditions && result.provider.conditions.length > 0 && (
                          <p className="text-lg font-semibold" style={{ color: 'var(--color-accent)' }}>
                            {result.provider.conditions.map(c => c.condition.name).join(', ')}
                          </p>
                        )}
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2 ml-4">
                      {result.provider.acceptsNewPatients ? (
                        <span 
                          className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold shadow-sm relative"
                          style={{
                            color: 'white'
                          }}
                        >
                          <span 
                            className="absolute inset-0 rounded-full"
                            style={{
                              backgroundColor: 'var(--color-success)',
                              opacity: 0.7
                            }}
                          />
                          <span className="relative flex items-center">
                            <Clock className="w-3.5 h-3.5 mr-1.5" />
                            New Patients
                          </span>
                        </span>
                      ) : (
                        <span 
                          className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold shadow-sm"
                          style={{
                            backgroundColor: 'var(--color-error)',
                            color: 'white'
                          }}
                        >
                          Not Accepting
                        </span>
                      )}
                      {result.provider.requiresReferral && (
                        <span 
                          className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold shadow-sm relative"
                          style={{
                            color: 'white'
                          }}
                        >
                          <span 
                            className="absolute inset-0 rounded-full"
                            style={{
                              backgroundColor: 'var(--color-warning)',
                              opacity: 0.7
                            }}
                          />
                          <span className="relative">Referral Required</span>
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Collapsible Content */}
                  {isExpanded && (
                    <div className="space-y-6">

                  {/* APP's Section */}
                  {result.provider.apps && result.provider.apps.length > 0 && (
                    <div className="mb-6 pb-6 border-b" style={{ borderColor: 'var(--color-border)' }}>
                      <div className="flex items-center gap-2 mb-4">
                        <div className="p-2 rounded-lg" style={{ backgroundColor: 'var(--color-bg-secondary)' }}>
                          <Stethoscope className="w-5 h-5" style={{ color: 'var(--color-accent)' }} />
                        </div>
                        <h4 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                          APP's
                        </h4>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {result.provider.apps.map((app) => (
                          <span
                            key={app.id}
                            className="inline-flex items-center px-2.5 py-1 rounded text-xs font-medium shadow-sm"
                            style={{
                              backgroundColor: '#f97316',
                              color: 'white'
                            }}
                          >
                            {app.firstName} {app.lastName}{app.title ? ` - ${app.title}` : ''}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Doctor Section (for APPs) */}
                  {(() => {
                    const doctors = (result.provider as { supervisingProviders?: Array<{ id: string; firstName: string; lastName: string; title?: string }> }).supervisingProviders?.length
                      ? (result.provider as { supervisingProviders: Array<{ id: string; firstName: string; lastName: string; title?: string }> }).supervisingProviders
                      : result.provider.supervisingProvider
                        ? [result.provider.supervisingProvider]
                        : [];
                    if (doctors.length === 0) return null;
                    return (
                      <div className="mb-6 pb-6 border-b" style={{ borderColor: 'var(--color-border)' }}>
                        <div className="flex items-center gap-2 mb-4">
                          <div className="p-2 rounded-lg" style={{ backgroundColor: 'var(--color-bg-secondary)' }}>
                            <Users className="w-5 h-5" style={{ color: 'var(--color-accent)' }} />
                          </div>
                          <h4 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                            Doctor
                          </h4>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {doctors.map((doc) => (
                            <span
                              key={doc.id}
                              className="inline-flex items-center px-2.5 py-1 rounded text-xs font-medium shadow-sm"
                              style={{
                                backgroundColor: '#3b82f6',
                                color: 'white'
                              }}
                            >
                              {doc.firstName} {doc.lastName}{doc.title ? ` - ${doc.title}` : ''}
                            </span>
                          ))}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Additional Information */}
                  {(result.provider.schedulingInstructions || result.provider.insuranceNotes || result.displayNotes) && (
                    <div className="mb-6 pb-6 border-b" style={{ borderColor: 'var(--color-border)' }}>
                      <h4 className="text-lg font-semibold mb-4" style={{ color: 'var(--color-text-primary)' }}>
                        Additional Information
                      </h4>
                      <div 
                        className="p-5 rounded-xl border space-y-3"
                        style={{
                          backgroundColor: 'var(--color-bg-secondary)',
                          borderColor: 'var(--color-border)'
                        }}
                      >
                        {result.provider.schedulingInstructions && (
                          <div className="text-sm">
                            <div className="font-semibold mb-1" style={{ color: 'var(--color-text-primary)' }}>Scheduling:</div>
                            <RichTextDisplay
                              content={result.provider.schedulingInstructions}
                              style={{ color: 'var(--color-text-secondary)' }}
                            />
                          </div>
                        )}
                        {result.provider.insuranceNotes && (
                          <div className="text-sm">
                            <div className="font-semibold mb-1" style={{ color: 'var(--color-text-primary)' }}>Insurance:</div>
                            <RichTextDisplay
                              content={result.provider.insuranceNotes}
                              style={{ color: 'var(--color-text-secondary)' }}
                            />
                          </div>
                        )}
                        
                      </div>
                    </div>
                  )}

                  {/* Contact Information */}
                  <div className="mb-6 pb-6 border-b" style={{ borderColor: 'var(--color-border)' }}>
                    <div className="flex items-center gap-2 mb-4">
                      <div className="p-2 rounded-lg" style={{ backgroundColor: 'var(--color-bg-secondary)' }}>
                        <Phone className="w-5 h-5" style={{ color: 'var(--color-accent)' }} />
                      </div>
                      <h4 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                        Contact Information
                      </h4>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Phone Numbers with Names and Locations */}
                      {result.provider.phoneNumbers && result.provider.phoneNumbers.length > 0 ? (
                        result.provider.phoneNumbers.map((phoneNumber) => (
                          <div 
                            key={phoneNumber.id} 
                            className="p-4 rounded-lg border"
                            style={{
                              backgroundColor: 'var(--color-bg-secondary)',
                              borderColor: 'var(--color-border)'
                            }}
                          >
                            <div className="flex items-start gap-3">
                              <Phone className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: 'var(--color-accent)' }} />
                              <div className="flex-1">
                                <a 
                                  href={`tel:${phoneNumber.phone}`} 
                                  className="text-base font-semibold transition-colors block mb-1"
                                  style={{ color: 'var(--color-accent)' }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.color = 'var(--color-accent-hover)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.color = 'var(--color-accent)';
                                  }}
                                >
                                  {formatPhone(phoneNumber.phone)}
                                </a>
                                <div className="text-sm space-y-1" style={{ color: 'var(--color-text-secondary)' }}>
                                  <div className="font-medium">{phoneNumber.name}</div>
                                  {phoneNumber.location && (
                                    <>
                                      <div>{phoneNumber.location.name}</div>
                                      {phoneNumber.location.physicalAddress && (
                                        <div className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                                          {phoneNumber.location.physicalAddress}
                                        </div>
                                      )}
                                      {(phoneNumber.location.city || phoneNumber.location.state || phoneNumber.location.zipCode) && (
                                        <div className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                                          {[
                                            phoneNumber.location.city,
                                            phoneNumber.location.state?.code,
                                            phoneNumber.location.zipCode
                                          ].filter(Boolean).join(', ')}
                                        </div>
                                      )}
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : result.provider.phone ? (
                        <div 
                          className="p-4 rounded-lg border flex items-center gap-3"
                          style={{
                            backgroundColor: 'var(--color-bg-secondary)',
                            borderColor: 'var(--color-border)'
                          }}
                        >
                          <Phone className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--color-accent)' }} />
                          <a 
                            href={`tel:${result.provider.phone}`} 
                            className="text-base font-semibold transition-colors"
                            style={{ color: 'var(--color-accent)' }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.color = 'var(--color-accent-hover)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.color = 'var(--color-accent)';
                            }}
                          >
                            {formatPhone(result.provider.phone)}
                          </a>
                        </div>
                      ) : null}
                      {result.provider.email && (
                        <div 
                          className="p-4 rounded-lg border flex items-center gap-3"
                          style={{
                            backgroundColor: 'var(--color-bg-secondary)',
                            borderColor: 'var(--color-border)'
                          }}
                        >
                          <Mail className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--color-accent)' }} />
                          <a 
                            href={`mailto:${result.provider.email}`} 
                            className="text-base font-semibold transition-colors break-all"
                            style={{ color: 'var(--color-accent)' }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.color = 'var(--color-accent-hover)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.color = 'var(--color-accent)';
                            }}
                          >
                            {result.provider.email}
                          </a>
                        </div>
                      )}
                      {result.provider.website && (
                        <div 
                          className="p-4 rounded-lg border flex items-center gap-3"
                          style={{
                            backgroundColor: 'var(--color-bg-secondary)',
                            borderColor: 'var(--color-border)'
                          }}
                        >
                          <ExternalLink className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--color-accent)' }} />
                          <a 
                            href={result.provider.website} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="text-base font-semibold transition-colors"
                            style={{ color: 'var(--color-accent)' }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.color = 'var(--color-accent-hover)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.color = 'var(--color-accent)';
                            }}
                          >
                            Visit Website
                          </a>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Locations & Office Details */}
                  {((result.provider.officeSuite || result.provider.officePhone || 
                    result.provider.clinicalPhone || result.provider.frontDeskPhone) ||
                    (result.provider.locations && result.provider.locations.length > 0)) && (
                    <div className="mb-6 pb-6 border-b" style={{ borderColor: 'var(--color-border)' }}>
                      <div className="flex items-center gap-2 mb-4">
                        <div className="p-2 rounded-lg" style={{ backgroundColor: 'var(--color-bg-secondary)' }}>
                          <MapPin className="w-5 h-5" style={{ color: 'var(--color-accent)' }} />
                        </div>
                        <h4 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                          Locations & Office Details
                        </h4>
                      </div>
                      <div className="space-y-4">
                        {/* Office Details */}
                        {(result.provider.officeSuite || result.provider.officePhone || 
                          result.provider.clinicalPhone || result.provider.frontDeskPhone) && (
                          <div 
                            className="p-5 rounded-xl border"
                            style={{
                              backgroundColor: 'var(--color-bg-secondary)',
                              borderColor: 'var(--color-border)'
                            }}
                          >
                            <div className="flex items-center gap-2 mb-3">
                              <Building className="w-4 h-4" style={{ color: 'var(--color-accent)' }} />
                              <h5 className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                                Main Office
                              </h5>
                            </div>
                            <div className="space-y-3 text-sm">
                              {result.provider.officeSuite && (
                                <div>
                                  <span className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>Suite: </span>
                                  <span style={{ color: 'var(--color-text-secondary)' }}>
                                    {result.provider.officeSuite}
                                  </span>
                                </div>
                              )}
                              {result.provider.officePhone && (
                                <div className="flex items-center gap-2">
                                  <Phone className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--color-accent)' }} />
                                  <span className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>Office: </span>
                                  <a 
                                    href={`tel:${result.provider.officePhone}`} 
                                    className="font-medium transition-colors"
                                    style={{ color: 'var(--color-accent)' }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.color = 'var(--color-accent-hover)';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.color = 'var(--color-accent)';
                                    }}
                                  >
                                    {formatPhone(result.provider.officePhone)}
                                  </a>
                                </div>
                              )}
                              {result.provider.clinicalPhone && (
                                <div className="flex items-center gap-2">
                                  <Phone className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--color-accent)' }} />
                                  <span className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>Clinical: </span>
                                  <a 
                                    href={`tel:${result.provider.clinicalPhone}`} 
                                    className="font-medium transition-colors"
                                    style={{ color: 'var(--color-accent)' }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.color = 'var(--color-accent-hover)';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.color = 'var(--color-accent)';
                                    }}
                                  >
                                    {formatPhone(result.provider.clinicalPhone)}
                                  </a>
                                </div>
                              )}
                              {result.provider.frontDeskPhone && (
                                <div className="flex items-center gap-2">
                                  <Phone className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--color-accent)' }} />
                                  <span className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>Front Desk: </span>
                                  <a 
                                    href={`tel:${result.provider.frontDeskPhone}`} 
                                    className="font-medium transition-colors"
                                    style={{ color: 'var(--color-accent)' }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.color = 'var(--color-accent-hover)';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.color = 'var(--color-accent)';
                                    }}
                                  >
                                    {formatPhone(result.provider.frontDeskPhone)}
                                  </a>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        {/* All Locations */}
                        {result.provider.locations && result.provider.locations.length > 0 && (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {result.provider.locations.map((providerLocation) => (
                              <div
                                key={providerLocation.id}
                                className="p-5 rounded-xl border shadow-sm"
                                style={{
                                  backgroundColor: 'var(--color-bg-secondary)',
                                  borderColor: 'var(--color-border)'
                                }}
                              >
                                <div className="flex items-start gap-3">
                                  <MapPin className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: 'var(--color-accent)' }} />
                                  <div className="flex-1">
                                    <div className="font-semibold text-base mb-2" style={{ color: 'var(--color-text-primary)' }}>
                                      {providerLocation.location.name}
                                    </div>
                                    <div className="space-y-1.5 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                                      {providerLocation.location.physicalAddress && (
                                        <div>{providerLocation.location.physicalAddress}</div>
                                      )}
                                      {(providerLocation.location.city || providerLocation.location.state || providerLocation.location.zipCode) && (
                                        <div>
                                          {[
                                            providerLocation.location.city,
                                            providerLocation.location.state?.code,
                                            providerLocation.location.zipCode
                                          ].filter(Boolean).join(', ')}
                                        </div>
                                      )}
                                      {providerLocation.address && (
                                        <div>
                                          {providerLocation.address}
                                          {providerLocation.suite && `, Suite ${providerLocation.suite}`}
                                        </div>
                                      )}
                                      {providerLocation.phone && (
                                        <div className="flex items-center gap-1.5 mt-2">
                                          <Phone className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--color-accent)' }} />
                                          <a 
                                            href={`tel:${providerLocation.phone}`} 
                                            className="font-medium transition-colors"
                                            style={{ color: 'var(--color-accent)' }}
                                            onMouseEnter={(e) => {
                                              e.currentTarget.style.color = 'var(--color-accent-hover)';
                                            }}
                                            onMouseLeave={(e) => {
                                              e.currentTarget.style.color = 'var(--color-accent)';
                                            }}
                                          >
                                            {formatPhone(providerLocation.phone)}
                                          </a>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Services & Capabilities */}
                  {((result.provider.conditions && result.provider.conditions.length > 0) ||
                    (result.provider.languages && result.provider.languages.length > 0) ||
                    (result.provider.minimumAge !== null || result.provider.maximumAge !== null || result.provider.ageRestrictionNotes)) && (
                    <div className="mb-6 pb-6 border-b" style={{ borderColor: 'var(--color-border)' }}>
                      <div className="flex items-center gap-2 mb-4">
                        <div className="p-2 rounded-lg" style={{ backgroundColor: 'var(--color-bg-secondary)' }}>
                          <Stethoscope className="w-5 h-5" style={{ color: 'var(--color-accent)' }} />
                        </div>
                        <h4 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                          Services & Capabilities
                        </h4>
                      </div>
                      <div className="space-y-6">
                        {/* Specialties */}
                        {result.provider.conditions && result.provider.conditions.length > 0 && (() => {
                          const groupedByCategory = result.provider.conditions
                            .filter(condition => condition.condition.category)
                            .reduce((acc: Record<string, typeof result.provider.conditions>, condition) => {
                              const category = condition.condition.category!;
                              if (!acc[category]) {
                                acc[category] = [];
                              }
                              acc[category].push(condition);
                              return acc;
                            }, {});

                          const uncategorizedConditions = result.provider.conditions.filter(
                            condition => !condition.condition.category
                          );

                          return (
                            <div>
                              <h5 className="text-sm font-semibold mb-3" style={{ color: 'var(--color-text-secondary)' }}>
                                Specialties
                              </h5>
                              <div className="space-y-4">
                                {Object.entries(groupedByCategory).map(([category, conditions]) => (
                                  <div key={category}>
                                    <h6 className="text-xs font-semibold mb-2 uppercase tracking-wide" style={{ color: 'var(--color-text-muted)' }}>
                                      {category}
                                    </h6>
                                    <div className="flex flex-wrap gap-1.5">
                                      {conditions.map((providerCondition) => (
                                        <div
                                          key={providerCondition.id}
                                          className="px-2 py-1 rounded border shadow-sm"
                                          style={{
                                            backgroundColor: 'var(--color-bg-secondary)',
                                            color: 'var(--color-text-primary)',
                                            borderColor: 'var(--color-border)'
                                          }}
                                        >
                                          <div className="font-semibold text-xs">{providerCondition.condition.name}</div>
                                          {providerCondition.notes && (
                                            <div className="text-xs mt-1" style={{ color: 'var(--color-text-secondary)' }}>
                                              {providerCondition.notes}
                                            </div>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                ))}
                                {uncategorizedConditions.length > 0 && (
                                  <div className="flex flex-wrap gap-1.5">
                                    {uncategorizedConditions.map((providerCondition) => (
                                      <div
                                        key={providerCondition.id}
                                        className="px-2 py-1 rounded border shadow-sm"
                                        style={{
                                          backgroundColor: 'var(--color-bg-secondary)',
                                          color: 'var(--color-text-primary)',
                                          borderColor: 'var(--color-border)'
                                        }}
                                      >
                                        <div className="font-semibold text-xs">{providerCondition.condition.name}</div>
                                        {providerCondition.notes && (
                                          <div className="text-xs mt-1" style={{ color: 'var(--color-text-secondary)' }}>
                                            {providerCondition.notes}
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })()}
                        {/* Languages */}
                        {result.provider.languages && result.provider.languages.length > 0 && (
                          <div>
                            <h5 className="text-sm font-semibold mb-3" style={{ color: 'var(--color-text-secondary)' }}>
                              Languages Spoken
                            </h5>
                            <div className="flex flex-wrap gap-3">
                              {result.provider.languages.map((providerLanguage) => (
                                <span
                                  key={providerLanguage.id}
                                  className="px-4 py-2 rounded-xl text-sm font-medium shadow-sm"
                                  style={{
                                    backgroundColor: 'var(--color-bg-secondary)',
                                    color: 'var(--color-text-primary)',
                                    border: '1px solid var(--color-border)'
                                  }}
                                >
                                  {providerLanguage.language.name}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        {/* Age Restrictions */}
                        <div>
                          <h5 className="text-sm font-semibold mb-3" style={{ color: 'var(--color-text-secondary)' }}>
                            Age Restrictions
                          </h5>
                          <div 
                            className="inline-flex items-center gap-4 px-5 py-3 rounded-xl mb-3"
                            style={{
                              backgroundColor: 'var(--color-bg-secondary)',
                              border: '1px solid var(--color-border)'
                            }}
                          >
                            {(result.provider.minimumAge === null || result.provider.minimumAge === undefined) &&
                             (result.provider.maximumAge === null || result.provider.maximumAge === undefined) ? (
                              <div className="text-sm">
                                <span className="font-bold text-base" style={{ color: 'var(--color-text-muted)' }}>
                                  N/A
                                </span>
                              </div>
                            ) : (
                              <>
                                {result.provider.minimumAge !== null && result.provider.minimumAge !== undefined ? (
                                  <div className="text-sm">
                                    <span style={{ color: 'var(--color-text-secondary)' }}>Minimum age: </span>
                                    <span className="font-bold text-base" style={{ color: 'var(--color-text-primary)' }}>
                                      {result.provider.minimumAge}
                                    </span>
                                  </div>
                                ) : (
                                  <div className="text-sm">
                                    <span style={{ color: 'var(--color-text-secondary)' }}>Minimum age: </span>
                                    <span className="font-bold text-base" style={{ color: 'var(--color-text-muted)' }}>
                                      N/A
                                    </span>
                                  </div>
                                )}
                                <div className="w-1 h-1 rounded-full" style={{ backgroundColor: 'var(--color-text-muted)' }}></div>
                                {result.provider.maximumAge !== null && result.provider.maximumAge !== undefined ? (
                                  <div className="text-sm">
                                    <span style={{ color: 'var(--color-text-secondary)' }}>Maximum age: </span>
                                    <span className="font-bold text-base" style={{ color: 'var(--color-text-primary)' }}>
                                      {result.provider.maximumAge}
                                    </span>
                                  </div>
                                ) : (
                                  <div className="text-sm">
                                    <span style={{ color: 'var(--color-text-secondary)' }}>Maximum age: </span>
                                    <span className="font-bold text-base" style={{ color: 'var(--color-text-muted)' }}>
                                      N/A
                                    </span>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                          {result.provider.ageRestrictionNotes && (
                            <div 
                              className="px-5 py-3 rounded-xl border"
                              style={{
                                backgroundColor: 'var(--color-bg-secondary)',
                                borderColor: 'var(--color-border)'
                              }}
                            >
                              <p className="text-sm leading-relaxed" style={{ color: 'var(--color-text-secondary)' }}>
                                {result.provider.ageRestrictionNotes}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Insurance & Coverage */}
                  {result.provider.insurances && result.provider.insurances.length > 0 && (
                    <div className="mb-6 pb-6 border-b" style={{ borderColor: 'var(--color-border)' }}>
                      <div className="flex items-center gap-2 mb-4">
                        <div className="p-2 rounded-lg" style={{ backgroundColor: 'var(--color-bg-secondary)' }}>
                          <Shield className="w-5 h-5" style={{ color: 'var(--color-accent)' }} />
                        </div>
                        <h4 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                          Insurances Accepted
                        </h4>
                      </div>
                      <div className="flex flex-wrap gap-3">
                        {result.provider.insurances.map((providerInsurance) => (
                          <div
                            key={providerInsurance.id}
                            className="px-4 py-2.5 rounded-xl border shadow-sm"
                            style={{
                              backgroundColor: 'var(--color-bg-secondary)',
                              color: 'var(--color-text-primary)',
                              borderColor: 'var(--color-border)'
                            }}
                          >
                            <div className="font-semibold text-sm">{providerInsurance.insurance.name}</div>
                            {providerInsurance.notes && (
                              <div className="text-xs mt-1.5" style={{ color: 'var(--color-text-secondary)' }}>
                                {providerInsurance.notes}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Support Team */}
                  {(result.provider.maLvnName || result.provider.triageNurseName || result.provider.surgerySchedulerName || 
                    result.provider.practiceAdminName) && (
                    <div className="mb-6 pb-6 border-b" style={{ borderColor: 'var(--color-border)' }}>
                      <div className="flex items-center gap-2 mb-4">
                        <div className="p-2 rounded-lg" style={{ backgroundColor: 'var(--color-bg-secondary)' }}>
                          <UserCheck className="w-5 h-5" style={{ color: 'var(--color-accent)' }} />
                        </div>
                        <h4 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                          Support Team
                        </h4>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {result.provider.maLvnName && (
                          <div 
                            className="p-4 rounded-xl border"
                            style={{
                              backgroundColor: 'var(--color-bg-secondary)',
                              borderColor: 'var(--color-border)'
                            }}
                          >
                            <div className="text-xs font-semibold mb-1.5 uppercase tracking-wide" style={{ color: 'var(--color-text-muted)' }}>
                              MA/LVN
                            </div>
                            <div className="text-sm font-semibold mb-1" style={{ color: 'var(--color-text-primary)' }}>
                              {result.provider.maLvnName}
                            </div>
                            {result.provider.maLvnPhone && (
                              <a 
                                href={`tel:${result.provider.maLvnPhone}`} 
                                className="text-sm font-medium transition-colors flex items-center gap-1.5"
                                style={{ color: 'var(--color-accent)' }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.color = 'var(--color-accent-hover)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.color = 'var(--color-accent)';
                                }}
                              >
                                <Phone className="w-3.5 h-3.5" />
                                {formatPhone(result.provider.maLvnPhone)}
                              </a>
                            )}
                          </div>
                        )}
                        {result.provider.triageNurseName && (
                          <div 
                            className="p-4 rounded-xl border"
                            style={{
                              backgroundColor: 'var(--color-bg-secondary)',
                              borderColor: 'var(--color-border)'
                            }}
                          >
                            <div className="text-xs font-semibold mb-1.5 uppercase tracking-wide" style={{ color: 'var(--color-text-muted)' }}>
                              Triage Nurse
                            </div>
                            <div className="text-sm font-semibold mb-1" style={{ color: 'var(--color-text-primary)' }}>
                              {result.provider.triageNurseName}
                            </div>
                            {result.provider.triageNursePhone && (
                              <a 
                                href={`tel:${result.provider.triageNursePhone}`} 
                                className="text-sm font-medium transition-colors flex items-center gap-1.5"
                                style={{ color: 'var(--color-accent)' }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.color = 'var(--color-accent-hover)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.color = 'var(--color-accent)';
                                }}
                              >
                                <Phone className="w-3.5 h-3.5" />
                                {formatPhone(result.provider.triageNursePhone)}
                              </a>
                            )}
                          </div>
                        )}
                        {result.provider.surgerySchedulerName && (
                          <div 
                            className="p-4 rounded-xl border"
                            style={{
                              backgroundColor: 'var(--color-bg-secondary)',
                              borderColor: 'var(--color-border)'
                            }}
                          >
                            <div className="text-xs font-semibold mb-1.5 uppercase tracking-wide" style={{ color: 'var(--color-text-muted)' }}>
                              Surgery Scheduler
                            </div>
                            <div className="text-sm font-semibold mb-1" style={{ color: 'var(--color-text-primary)' }}>
                              {result.provider.surgerySchedulerName}
                            </div>
                            {result.provider.surgerySchedulerPhone && (
                              <a 
                                href={`tel:${result.provider.surgerySchedulerPhone}`} 
                                className="text-sm font-medium transition-colors flex items-center gap-1.5"
                                style={{ color: 'var(--color-accent)' }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.color = 'var(--color-accent-hover)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.color = 'var(--color-accent)';
                                }}
                              >
                                <Phone className="w-3.5 h-3.5" />
                                {formatPhone(result.provider.surgerySchedulerPhone)}
                              </a>
                            )}
                          </div>
                        )}
                        {result.provider.practiceAdminName && (
                          <div 
                            className="p-4 rounded-xl border"
                            style={{
                              backgroundColor: 'var(--color-bg-secondary)',
                              borderColor: 'var(--color-border)'
                            }}
                          >
                            <div className="text-xs font-semibold mb-1.5 uppercase tracking-wide" style={{ color: 'var(--color-text-muted)' }}>
                              Practice Admin
                            </div>
                            <div className="text-sm font-semibold mb-1" style={{ color: 'var(--color-text-primary)' }}>
                              {result.provider.practiceAdminName}
                            </div>
                            {result.provider.practiceAdminPhone && (
                              <a 
                                href={`tel:${result.provider.practiceAdminPhone}`} 
                                className="text-sm font-medium transition-colors flex items-center gap-1.5"
                                style={{ color: 'var(--color-accent)' }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.color = 'var(--color-accent-hover)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.color = 'var(--color-accent)';
                                }}
                              >
                                <Phone className="w-3.5 h-3.5" />
                                {formatPhone(result.provider.practiceAdminPhone)}
                              </a>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Credentials */}
                  {(result.provider.licenseNumber || result.provider.npiNumber) && (
                    <div className="mb-0 pb-6 border-b-0" style={{ borderColor: 'var(--color-border)' }}>
                      <div className="flex items-center gap-2 mb-4">
                        <div className="p-2 rounded-lg" style={{ backgroundColor: 'var(--color-bg-secondary)' }}>
                          <Award className="w-5 h-5" style={{ color: 'var(--color-accent)' }} />
                        </div>
                        <h4 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                          Credentials
                        </h4>
                      </div>
                      <div 
                        className="inline-flex flex-col gap-3 px-5 py-4 rounded-xl"
                        style={{
                          backgroundColor: 'var(--color-bg-secondary)',
                          border: '1px solid var(--color-border)'
                        }}
                      >
                        {result.provider.licenseNumber && (
                          <div className="text-sm">
                            <span style={{ color: 'var(--color-text-secondary)' }}>License: </span>
                            <span className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                              {result.provider.licenseNumber}
                            </span>
                          </div>
                        )}
                        {result.provider.npiNumber && (
                          <div className="text-sm">
                            <span style={{ color: 'var(--color-text-secondary)' }}>NPI: </span>
                            <span className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                              {result.provider.npiNumber}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Provider Notes */}
                   {result.provider.notes && result.provider.notes.length > 0 && (
                     <div className="border-t pt-6" style={{ borderColor: 'var(--color-border)' }}>
                       <div className="flex items-center gap-2 mb-4">
                         <div className="p-2 rounded-lg" style={{ backgroundColor: 'var(--color-bg-secondary)' }}>
                           <FileText className="w-5 h-5" style={{ color: 'var(--color-accent)' }} />
                         </div>
                         <h4 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                           Provider Notes
                         </h4>
                       </div>
                       <div className="space-y-3">
                         {[...result.provider.notes]
                           .sort((a, b) => a.order - b.order)
                           .map((note) => (
                             <div 
                               key={note.id} 
                               className="p-4 rounded-xl border shadow-sm"
                               style={{
                                 backgroundColor: 'var(--color-bg-secondary)',
                                 borderColor: 'var(--color-border)'
                               }}
                             >
                               <CollapsibleRichText
                                 content={note.content}
                                 maxLines={5}
                                 style={{ color: 'var(--color-text-primary)' }}
                               />
                               {note.category && (
                                 <span 
                                   className="inline-block mt-3 px-3 py-1 text-xs font-medium rounded-full"
                                   style={{
                                     backgroundColor: 'var(--color-bg-primary)',
                                     color: 'var(--color-text-muted)',
                                     border: '1px solid var(--color-border)'
                                   }}
                                 >
                                   {note.category}
                                 </span>
                               )}
                             </div>
                           ))}
                      </div>
                    </div>
                  )}
                </div>
                  )}
                </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      </div>
    </div>
  );
};
